import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { gengql } from '@/__generated__';

const DASHBOARD_BILLING = gengql(/* GraphQL */ `
  query dashboardBilling($billingAccountId: String!) {
    billingAccount(id: $billingAccountId) {
      id
      credit_balance {
        cents
        currency
      }
      price_per_credit {
        cents
        currency
      }
      subscription {
        id
        currentPeriodEnd
        currentPeriodStart
        isActive
        billingInterval
        daysUntilRenewal
        pricePerSeat {
          cents
          currency
        }
        quantity
      }
      active_purchases {
        amount {
          cents
          currency
        }
        remaining {
          cents
          currency
        }
        expires_at
        purchased_at
      }
    }
  }
`);

export interface BillingData {
  creditBalanceCents: number;
  // Sum of active (non-expired, non-zero remaining) purchase amounts. May be 0
  // when the only credits an account has come from legacy no-expiration grants
  // or expired purchases — UI should drop the "N / total" fraction in that
  // case rather than render a misleading "X / X".
  totalCreditsCents: number;
  hasActivePurchases: boolean;
  pricePerCreditCents: number | null;
  renewalDate: string | null;
  billingInterval: string | null;
  isSubscriptionActive: boolean;
  // SMB subscription price per seat, in cents, on the subscription's billing
  // interval (monthly when planBillingInterval is MONTH, annual when YEAR). Null
  // when the account has no subscription (credit accounts). Drives the SMB
  // header's headline figure, normalized to a monthly per-seat price.
  planPerSeatCents: number | null;
  // Currency of planPerSeatCents (e.g. "USD"). Null when the account has no
  // subscription. Pairs with planPerSeatCents for money() formatting.
  planPerSeatCurrency: string | null;
  // SMB subscription seat count. Null when the account has no subscription.
  // Rendered as the header's "{n} seats" subtext.
  planQuantity: number | null;
  // SMB subscription billing interval (MONTH/YEAR). Null when the account has no
  // subscription. Drives the monthly normalization of the per-seat price and the
  // "billed annually" note.
  planBillingInterval: string | null;
}

// parseInt(...) || null would coerce a legitimate 0 (e.g. a $0 price) to null,
// reporting it as "unknown". Keep 0 distinct from missing: null only when the
// value is absent or non-numeric.
function parseOptionalCents(cents: string | null | undefined): number | null {
  if (cents === null || cents === undefined) return null;
  const n = parseInt(cents, 10);
  return Number.isFinite(n) ? n : null;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useDashboardBilling(billingAccountId: string | undefined) {
  const { data, loading, error } = useQuery(DASHBOARD_BILLING, {
    variables: { billingAccountId: billingAccountId! },
    skip: !billingAccountId,
    fetchPolicy: 'cache-and-network',
  });

  const billing = useMemo((): BillingData | null => {
    const account = data?.billingAccount;
    if (!account) return null;

    const creditBalanceCents = parseInt(account.credit_balance?.cents ?? '0', 10) || 0;

    // active_purchases is server-filtered: only purchases that still
    // contribute to available credits (non-expired, remaining > 0).
    const activePurchases = account.active_purchases ?? [];
    const totalCreditsCents = activePurchases.reduce((sum, p) => {
      if (!p) return sum;
      return sum + (parseInt(p.amount?.cents ?? '0', 10) || 0);
    }, 0);

    const pricePerCreditCents = parseOptionalCents(account.price_per_credit?.cents);

    return {
      creditBalanceCents,
      totalCreditsCents,
      // When credit balance exceeds active-purchase total, the account also
      // holds legacy no-expiration grants. The fraction "X / Y" would lie in
      // that case (X > Y), so drop it and render the balance alone.
      hasActivePurchases:
        activePurchases.length > 0 &&
        totalCreditsCents > 0 &&
        creditBalanceCents <= totalCreditsCents,
      pricePerCreditCents,
      renewalDate: account.subscription?.currentPeriodEnd ?? null,
      billingInterval: account.subscription?.billingInterval ?? null,
      isSubscriptionActive: account.subscription?.isActive ?? false,
      planPerSeatCents: parseOptionalCents(account.subscription?.pricePerSeat?.cents),
      planPerSeatCurrency: account.subscription?.pricePerSeat?.currency ?? null,
      planQuantity: account.subscription?.quantity ?? null,
      planBillingInterval: account.subscription?.billingInterval ?? null,
    };
  }, [data]);

  return { billing, loading, error };
}
